import jsPDF from 'jspdf';
import { toast } from "sonner";
import {
    formatScoringRubricText,
    formatQuestionScoreText,
    getCandidateResponseLabel,
    getResumeMatchDisplay,
    getReviewReasons,
    isReviewRequired,
} from "../../utils/analysisDisplay";

const REVIEW_EXPLANATION = "The evaluation is complete, but a person should look at it before acting on it. This is not a rejection.";

// Helper function to load image and convert to base64 (without Canvas processing)
const loadImageAsBase64 = (url) => {
    return fetch(url)
        .then(response => {
            if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.statusText}`);
            }
            return response.blob();
        })
        .then(blob => {
            return new Promise((resolve, reject) => {
                // Convert blob to base64 using FileReader
                const reader = new FileReader();
                reader.onloadend = () => {
                    // Get image dimensions using Image object
                    const img = new Image();
                    img.onload = () => {
                        resolve({
                            base64: reader.result,
                            width: img.width,
                            height: img.height
                        });
                    };
                    img.onerror = reject;
                    img.src = reader.result;
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        });
};

// Cache for logo image data
let logoCache = null;

// Helper function to add logo watermark to PDF page
const addLogoWatermark = async (doc) => {
    try {
        // Load logo if not cached
        if (!logoCache) {
            try {
                logoCache = await loadImageAsBase64('/june logo png.png');
            } catch (loadError) {
                console.error('Failed to load logo image:', loadError);
                return;
            }
        }
        
        if (!logoCache || !logoCache.base64) {
            console.warn('Logo cache is empty');
            return;
        }
        
        // Convert pixel dimensions to mm for display (assuming 96dpi: 1px ≈ 0.264583mm)
        // Scale down by 10x while maintaining aspect ratio
        const pxToMm = 0.264583;
        const imgWidth = (logoCache.width * pxToMm) / 10;
        const imgHeight = (logoCache.height * pxToMm) / 10;
        
        // Position at top-left corner (5mm from top and left)
        const x = 5; // 5mm from left
        const y = 5; // 5mm from top
        
        // Add image at top-left corner with original color (no transformations)
        doc.addImage(
            logoCache.base64,
            'PNG',
            x,
            y,
            imgWidth,
            imgHeight
        );
    } catch (error) {
        console.error('Failed to add logo watermark:', error);
        // Continue without logo if image fails to load
    }
};

const renderLines = async (doc, lines, x, yPosition, lineHeight) => {
    const pageHeight = doc.internal.pageSize.height;
    const bottomMargin = 20;
    let y = yPosition;

    for (const line of lines) {
        if (y + lineHeight > pageHeight - bottomMargin) {
            doc.addPage();
            await addLogoWatermark(doc);
            y = 20;
        }
        doc.text(line, x, y);
        y += lineHeight;
    }

    return y;
};

export const copyToClipboardHandler = (selectedApplicantForAnalysis, roleName = "", companyName = "") => {
    let text = "Name: " + selectedApplicantForAnalysis.name + "\n";
    
    // Add job title if available
    if (roleName) {
        text += "Job Title: " + roleName + "\n";
    }
    
    // Add company name if available
    if (companyName) {
        text += "Company Name: " + companyName + "\n";
    }
    
    text +=
        "Submitted: " +
        new Date(selectedApplicantForAnalysis.created_at).toLocaleDateString("en-US") +
        "\n\n\n";

    if (isReviewRequired(selectedApplicantForAnalysis?.ai_insights)) {
        text += `Needs human review\n${REVIEW_EXPLANATION}\n`;
        for (const reason of getReviewReasons(selectedApplicantForAnalysis.ai_insights)) {
            text += `- ${reason}\n`;
        }
        text += "\n\n";
    }

    // Add overall summary if available
    if (selectedApplicantForAnalysis?.ai_insights?.overall_summary) {
        text += "Summary\n";
        text += "-------\n";
        text += selectedApplicantForAnalysis.ai_insights.overall_summary + "\n\n\n";
    }

    const resumeMatch = getResumeMatchDisplay(selectedApplicantForAnalysis?.ai_insights);
    if (resumeMatch) {
        text += "Interview vs. Resume" + (resumeMatch.score != null ? ": " + resumeMatch.score + "/5" : "") + "\n";
        text += resumeMatch.summary + "\n\n\n";
    }

    if (selectedApplicantForAnalysis?.ai_insights?.questions_evaluation) {
        const responseLabel = getCandidateResponseLabel(selectedApplicantForAnalysis.ai_insights);
        for (const question of selectedApplicantForAnalysis.ai_insights.questions_evaluation) {
            text += `Question:${question.interview_question}`;
            text += "\n";
            text += `${question.is_pass_fail ? "Result" : "Match Rating"}: ${formatQuestionScoreText(question)}`;
            text += "\n";
            text += `${responseLabel}: ${question.candidate_response}`;
            text += "\n";
            text += `${question.scoring_rubric ? "Scoring Rubric" : "Legacy Desired Response"}:\n${formatScoringRubricText(question)}`;
            text += "\n";
            text += `Justification: ${question.justification}`;
            text += "\n\n\n";
        }
    }

    text += "Full Transcript\n";
    text += "---------------\n\n";
    selectedApplicantForAnalysis?.interview_responses
        ?.sort((a, b) => a.question_number - b.question_number)
        .forEach((item) => {
            text += `June: ${item.question}\n`;
            text += `Applicant: ${item.response || "No response provided."}\n\n`;
        });

    // Add Powered by June watermark
    text += "\nPowered by June";

    navigator.clipboard
        .writeText(text)
        .then(() => {
            toast.success("Copied to clipboard!");
        })
        .catch((err) => {
            toast.error("Failed to copy to clipboard");
        });
};

export const exportToPDFHandler = async (selectedApplicantForAnalysis, roleName = "", companyName = "") => {
    const doc = new jsPDF();
    const lineHeight = 10;
    let yPosition = 20;
    const margin = 20;
    const pageWidth = doc.internal.pageSize.width;

    // Add logo watermark to first page
    await addLogoWatermark(doc);

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    
    // Use passed roleName first, then fallback to data from selectedApplicantForAnalysis
    const jobName = roleName || selectedApplicantForAnalysis?.job?.role_name;
    
    // Use a simple title since job name is now displayed separately below
    const title = "Candidate Evaluation Report";
    doc.text(title, margin, yPosition);
    yPosition += lineHeight * 2;

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");

    doc.text(`Name: ${selectedApplicantForAnalysis.name}`, margin, yPosition);
    yPosition += lineHeight;
    
    // Add job title if available
    if (jobName) {
        doc.text(`Job Title: ${jobName}`, margin, yPosition);
        yPosition += lineHeight;
    }
    
    // Add company name if available
    if (companyName) {
        doc.text(`Company Name: ${companyName}`, margin, yPosition);
        yPosition += lineHeight;
    }
    
    doc.text(
        `Submitted: ${new Date(selectedApplicantForAnalysis.created_at).toLocaleDateString("en-US")}`,
        margin,
        yPosition
    );
    yPosition += lineHeight * 2;

    if (isReviewRequired(selectedApplicantForAnalysis?.ai_insights)) {
        doc.setFont("helvetica", "bold");
        yPosition = await renderLines(doc, ["Needs human review"], margin, yPosition, lineHeight);
        doc.setFont("helvetica", "normal");
        const reviewLines = [
            REVIEW_EXPLANATION,
            ...getReviewReasons(selectedApplicantForAnalysis.ai_insights).map(reason => `- ${reason}`),
        ];
        for (const text of reviewLines) {
            const lines = doc.splitTextToSize(text, pageWidth - margin * 2);
            yPosition = await renderLines(doc, lines, margin, yPosition, lineHeight);
        }
        yPosition += lineHeight;
    }

    if (selectedApplicantForAnalysis?.ai_insights?.overall_summary) {
        doc.setFont("helvetica", "bold");
        yPosition = await renderLines(doc, ["Summary"], margin, yPosition, lineHeight);
        doc.setFont("helvetica", "normal");

        const splitSummary = doc.splitTextToSize(
            selectedApplicantForAnalysis.ai_insights.overall_summary,
            pageWidth - margin * 2
        );
        yPosition = await renderLines(doc, splitSummary, margin, yPosition, lineHeight);
        yPosition += lineHeight;
    }

    const resumeMatch = getResumeMatchDisplay(selectedApplicantForAnalysis?.ai_insights);
    if (resumeMatch) {
        doc.setFont("helvetica", "bold");
        yPosition = await renderLines(doc, ["Interview vs. Resume"], margin, yPosition, lineHeight);
        doc.setFont("helvetica", "normal");
        const resumeMatchLine = (resumeMatch.score != null ? resumeMatch.score + "/5 — " : "") + resumeMatch.summary;
        const splitResumeMatch = doc.splitTextToSize(resumeMatchLine, pageWidth - margin * 2);
        yPosition = await renderLines(doc, splitResumeMatch, margin, yPosition, lineHeight);
        yPosition += lineHeight;
    }

    if (selectedApplicantForAnalysis?.ai_insights?.questions_evaluation) {
        const responseLabel = getCandidateResponseLabel(selectedApplicantForAnalysis.ai_insights);
        for (let index = 0; index < selectedApplicantForAnalysis.ai_insights.questions_evaluation.length; index++) {
            const question = selectedApplicantForAnalysis.ai_insights.questions_evaluation[index];

            doc.setFont("helvetica", "bold");
            const splitQuestion = doc.splitTextToSize(
                `Question ${index + 1}: ${question.interview_question}`,
                pageWidth - margin * 2
            );
            yPosition = await renderLines(doc, splitQuestion, margin, yPosition, lineHeight);

            doc.setFont("helvetica", "normal");
            yPosition = await renderLines(
                doc,
                [`${question.is_pass_fail ? "Result" : "Match Rating"}: ${formatQuestionScoreText(question)}`],
                margin,
                yPosition,
                lineHeight
            );
            yPosition += lineHeight * 0.5;

            doc.setFont("helvetica", "bold");
            yPosition = await renderLines(doc, [`${responseLabel}:`], margin, yPosition, lineHeight);
            doc.setFont("helvetica", "normal");
            const splitResponse = doc.splitTextToSize(
                question.candidate_response || "No response recorded.",
                pageWidth - margin * 2
            );
            yPosition = await renderLines(doc, splitResponse, margin, yPosition, lineHeight);
            yPosition += lineHeight;

            doc.setFont("helvetica", "bold");
            yPosition = await renderLines(doc, [question.scoring_rubric ? "Scoring Rubric:" : "Legacy Desired Response:"], margin, yPosition, lineHeight);
            doc.setFont("helvetica", "normal");
            const splitDesired = doc.splitTextToSize(
                formatScoringRubricText(question),
                pageWidth - margin * 2
            );
            yPosition = await renderLines(doc, splitDesired, margin, yPosition, lineHeight);
            yPosition += lineHeight;

            doc.setFont("helvetica", "bold");
            yPosition = await renderLines(doc, ["Justification:"], margin, yPosition, lineHeight);
            doc.setFont("helvetica", "normal");
            const splitJustification = doc.splitTextToSize(
                question.justification,
                pageWidth - margin * 2
            );
            yPosition = await renderLines(doc, splitJustification, margin, yPosition, lineHeight);
            yPosition += lineHeight;
        }
    }

    // Add Full Transcript section
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    yPosition = await renderLines(doc, ["Full Transcript"], margin, yPosition, lineHeight);
    yPosition += lineHeight;

    doc.setFont("helvetica", "normal");
    const sortedResponses = selectedApplicantForAnalysis?.interview_responses
        ?.sort((a, b) => a.question_number - b.question_number) || [];

    for (const item of sortedResponses) {
        doc.setFont("helvetica", "bold");
        yPosition = await renderLines(doc, ["June:"], margin, yPosition, lineHeight);

        doc.setFont("helvetica", "normal");
        const splitQuestion = doc.splitTextToSize(
            item.question,
            pageWidth - margin * 2
        );
        yPosition = await renderLines(doc, splitQuestion, margin, yPosition, lineHeight);

        doc.setFont("helvetica", "bold");
        yPosition = await renderLines(doc, ["Applicant:"], margin, yPosition, lineHeight);

        doc.setFont("helvetica", "normal");
        const splitResponse = doc.splitTextToSize(
            item.response || "No response provided.",
            pageWidth - margin * 2
        );
        yPosition = await renderLines(doc, splitResponse, margin, yPosition, lineHeight);
        yPosition += lineHeight;
    }

    doc.save(`${selectedApplicantForAnalysis.name.replace(/\s+/g, "_")}_evaluation.pdf`);
};

// Helper function to delay execution
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to download PDF with retry logic
const downloadWithRetry = async (applicant, roleName, companyName, maxRetries = 2) => {
    for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
        try {
            await exportToPDFHandler(applicant, roleName, companyName);
            return { success: true, applicant: applicant.name };
        } catch (error) {
            console.error(`Attempt ${attempt} failed for ${applicant.name}:`, error);
            
            if (attempt === maxRetries + 1) {
                return { success: false, applicant: applicant.name, error };
            }
            
            // Wait before retrying
            await delay(1000);
        }
    }
};

// Bulk export multiple applicant analyses as individual PDF files (downloaded one by one)
export const bulkExportAnalysisToPDF = async (applicants, roleName = "", companyName = "") => {
    const results = {
        successful: [],
        failed: []
    };

    try {
        // Download PDFs one by one with retry logic
        for (let i = 0; i < applicants.length; i++) {
            const applicant = applicants[i];
            const result = await downloadWithRetry(applicant, roleName, companyName);
            
            if (result.success) {
                results.successful.push(result.applicant);
            } else {
                results.failed.push(result.applicant);
            }
            
            // Add a delay between downloads (except after the last one)
            if (i < applicants.length - 1) {
                await delay(500); // 500ms delay between downloads
            }
        }
        
        // Show appropriate success/warning message based on results
        if (results.successful.length === applicants.length) {
            toast.success(`Successfully downloaded ${results.successful.length} evaluation report${results.successful.length > 1 ? 's' : ''}`);
        } else if (results.successful.length > 0) {
            toast.warning(`Downloaded ${results.successful.length} of ${applicants.length} reports. ${results.failed.length} failed.`);
        } else {
            toast.error(`Failed to download all ${applicants.length} reports.`);
        }
    } catch (error) {
        console.error('Error during bulk PDF export:', error);
        toast.error('Failed to download evaluation reports');
    }
};
